import React from 'react';

/**
 * One option builder for every account picker (ONE_CHART_PLAN.md OC-5).
 *
 * Every company runs on the full BAS 2026 chart — 1 280-odd accounts. A
 * novice should not scroll through all of them to find the ten they use, and
 * a reduced chart was rejected as the way to get there. So the pickers show a
 * default view instead: BAS's own basuppsättning (`is_base_selection`, 380
 * accounts) ∪ accounts that have ever carried anything (`has_activity`) ∪ the
 * company's own accounts (`is_base_plan` 0, which is what an SIE import
 * creates). "Visa alla konton" opens the rest. Archived accounts never arrive
 * here — the backend's picker query hides them (I6).
 *
 * The rows come from GET /:companyId/accounting/accounts. When the backend
 * could not resolve the basuppsättning (no chart marker), every row carries
 * `is_base_selection: null` and the default view is the whole list: degraded
 * to "everything", never to "something missing".
 */

export const OWN_ACCOUNT_LABEL = 'Eget konto';
export const RECENT_GROUP_LABEL = 'Senast använda';
export const RECENT_GROUP_SIZE = 8;

const asFlag = (value) => Number(value) === 1;
const fmt = (n) => new Intl.NumberFormat('sv-SE').format(Number(n) || 0);

/** A DATE column reaches the browser as an ISO string; keep the day only. */
function dateOnly(value) {
  if (!value) return null;
  if (typeof value === 'string') return value.slice(0, 10);
  try { return new Date(value).toISOString().slice(0, 10); } catch (e) { return null; }
}

/** Does the backend payload carry the basuppsättning flag at all? */
export function pickerScopeAvailable(accounts) {
  return (accounts || []).some((a) => a.is_base_selection !== null && a.is_base_selection !== undefined);
}

export function isOwnAccount(account) {
  return account.is_base_plan !== undefined && account.is_base_plan !== null && !asFlag(account.is_base_plan);
}

/** Belongs to the default view: basuppsättning, has activity, or own account. */
export function isDefaultPickerAccount(account) {
  return asFlag(account.is_base_selection) || asFlag(account.has_activity) || isOwnAccount(account);
}

/**
 * The option shape the pickers already use (`value`, `label`, `accountNumber`,
 * `accountName`, `accountType`) plus the picker facts. `valueKey` is
 * 'account_number' (bookkeeping surfaces) or 'account_id' (the verification
 * data input); the backend row is spread in last so nothing existing is lost.
 */
export function accountOption(account, { valueKey = 'account_number' } = {}) {
  return {
    value: account[valueKey],
    label: `${account.account_number} - ${account.account_name}`,
    accountNumber: account.account_number,
    accountName: account.account_name,
    accountType: account.account_type,
    isOwn: isOwnAccount(account),
    hasActivity: asFlag(account.has_activity),
    isBaseSelection: asFlag(account.is_base_selection),
    importSourceName: account.import_source_name || null,
    // UX_PLAN.md P4: when the account was last posted to (verifikat date) and
    // how often — the "Senast använda" group is ordered by these.
    lastUsedOn: dateOnly(account.last_used_on),
    postingCount: Number(account.posting_count) || 0,
    ...account,
  };
}

/**
 * Options for a picker.
 *
 * @param {Array} options rows or options (already through `accountOption`)
 * @param {object} opts
 * @param {boolean} [opts.showAll] "Visa alla konton" — the whole list
 * @param {Array} [opts.pinned] values that must stay selectable whatever the
 *   view: the rows' current selections (a template or an edited verifikat may
 *   point at an account outside the default view)
 */
export function buildAccountPickerOptions(options, { showAll = false, pinned = [] } = {}) {
  const list = options || [];
  if (showAll || !pickerScopeAvailable(list)) return list;
  const keep = new Set((pinned || []).filter((v) => v !== '' && v !== null && v !== undefined).map(String));
  return list.filter((o) => isDefaultPickerAccount(o) || keep.has(String(o.value)));
}

/**
 * Every account a bookkeeping entry currently points at, whatever shape holds
 * it (independent review 2026-09-05, F11): a proposal's own rows
 * (`customJournalEntries[].account`), a new-template input in `parameters`
 * and a legacy selection in `userDefinedAccounts`. Passed as `pinned` so a
 * selected account never leaves the picker's options when the view narrows
 * back to the default — a field whose stored value has no option renders
 * blank while the value is still posted.
 */
export function pinnedAccountValues(entry) {
  const looksLikeAccount = (v) => /^\d{4}$/.test(String(v ?? '').trim());
  const out = new Set();
  for (const line of (entry?.customJournalEntries || [])) {
    if (line && line.account !== undefined && line.account !== null && line.account !== '') out.add(String(line.account));
  }
  for (const bag of [entry?.parameters, entry?.userDefinedAccounts]) {
    for (const value of Object.values(bag || {})) {
      if (looksLikeAccount(value)) out.add(String(value).trim());
    }
  }
  return [...out];
}

/** How many accounts the default view holds, for the toggle's caption. */
export function countDefaultPickerAccounts(options) {
  const list = options || [];
  if (!pickerScopeAvailable(list)) return list.length;
  return list.filter(isDefaultPickerAccount).length;
}

/** Does the company have anything of its own in the chart yet — postings or own accounts? */
export function hasCompanyAccounts(options) {
  return (options || []).some((o) => asFlag(o.has_activity) || o.hasActivity || isOwnAccount(o));
}

/**
 * The caption under a picker, in the user's words (UX_PLAN.md P5). Never
 * "basuppsättningen" — BAS vocabulary a first-time user has not met. A fresh
 * company has no own or used accounts, so that clause is left out rather than
 * describing two empty sets.
 */
export function pickerScopeCaption({ showAll, defaultCount, totalCount, hasCompanyAccounts: hasOwn }) {
  if (showAll) {
    return { text: `Visar alla ${fmt(totalCount)} konton.`, toggle: 'Visa färre' };
  }
  const toggle = `Visa alla ${fmt(totalCount)} konton`;
  if (hasOwn) {
    return {
      text: `Visar ${fmt(defaultCount)} konton: de vanligaste i BAS-kontoplanen samt dina egna och använda konton.`,
      toggle,
    };
  }
  return { text: `Visar de ${fmt(defaultCount)} vanligaste kontona i BAS-kontoplanen.`, toggle };
}

// ---------------------------------------------------------------------------
// Search (UX_PLAN.md P1). Typing searches the WHOLE chart, whatever the browse
// view shows; the imported label counts, because a switching user knows the
// account by the name their old program gave it.
// ---------------------------------------------------------------------------

const fold = (value) => String(value ?? '').toLowerCase().trim().replace(/\s+/g, ' ');

/** Does one option match what the user typed? Number by prefix, names by substring. */
export function accountMatchesSearch(option, input) {
  const term = fold(input);
  if (!term) return true;
  const number = fold(option.accountNumber ?? option.value);
  const name = fold(option.accountName);
  const imported = fold(option.importSourceName);
  if (/^\d+$/.test(term)) {
    return number.startsWith(term) || name.includes(term) || imported.includes(term);
  }
  return name.includes(term) || imported.includes(term) || fold(option.label).includes(term) || number.includes(term);
}

/** react-select `filterOption` over the shared matcher. */
export function filterAccountOption(candidate, input) {
  return accountMatchesSearch(candidate?.data || candidate, input);
}

/** The options a search term keeps, from whatever list is passed. */
export function searchAccountOptions(options, input) {
  return (options || []).filter((o) => accountMatchesSearch(o, input));
}

/** react-select `noOptionsMessage`, in Swedish, saying how far the search reached. */
export function noAccountOptionsMessage({ inputValue } = {}, { totalCount = 0 } = {}) {
  if (!inputValue) return 'Inga konton att visa.';
  const reach = totalCount ? ` Sökningen omfattar alla ${fmt(totalCount)} konton.` : '';
  return `Inga konton matchar "${inputValue}".${reach}`;
}

/** The imported label is worth a second line only when it says something the chart name does not. */
export function showsImportLabel(option) {
  return Boolean(option?.importSourceName) && fold(option.importSourceName) !== fold(option.accountName);
}

// ---------------------------------------------------------------------------
// Order (UX_PLAN.md P4): the accounts this company actually posts to first.
// ---------------------------------------------------------------------------

/** Are these react-select groups rather than flat options? */
export function isGroupedOptions(options) {
  return Array.isArray(options) && options.length > 0 && Array.isArray(options[0]?.options);
}

/**
 * Group a picker view into "Senast använda" (the most recently posted-to
 * accounts, newest first, ties by posting count) and the rest. A company with
 * no postings yet gets the flat list — there is nothing recent to show. Order
 * only: nothing about which account is proposed changes here.
 */
export function groupPickerOptions(options, { recentSize = RECENT_GROUP_SIZE } = {}) {
  const list = options || [];
  const used = list.filter((o) => o.lastUsedOn);
  if (used.length === 0) return list;
  const recent = [...used]
    .sort((a, b) => String(b.lastUsedOn).localeCompare(String(a.lastUsedOn))
      || (b.postingCount || 0) - (a.postingCount || 0)
      || String(a.accountNumber).localeCompare(String(b.accountNumber)))
    .slice(0, recentSize);
  const recentValues = new Set(recent.map((o) => String(o.value)));
  const rest = list.filter((o) => !recentValues.has(String(o.value)));
  return [
    { label: RECENT_GROUP_LABEL, options: recent },
    { label: `Övriga konton (${fmt(rest.length)})`, options: rest },
  ];
}

/** The small chip that marks an own account in a menu row. */
export function OwnAccountChip() {
  return (
    <span
      className="account-option__own"
      style={{
        marginLeft: '0.5rem',
        padding: '0.05rem 0.45rem',
        borderRadius: '999px',
        fontSize: '0.6875rem',
        fontWeight: 600,
        background: 'var(--rgba-primary-1, #f4f8f5)',
        border: '1px solid var(--rgba-primary-3, #cfe3d3)',
        color: 'var(--primary, #44814E)',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
      }}
    >
      {OWN_ACCOUNT_LABEL}
    </span>
  );
}

/**
 * `formatOptionLabel` for react-select: the selected value renders as before
 * (number, or the full label — the caller says which), a menu row adds the
 * "Eget konto" chip for own accounts.
 */
export function formatAccountOption(option, { context } = {}, { valueDisplay = 'label' } = {}) {
  if (context === 'value') {
    return valueDisplay === 'number' ? option.accountNumber : option.label;
  }
  return (
    <span style={{ display: 'block' }}>
      <span>
        {option.label}
        {option.isOwn && <OwnAccountChip />}
      </span>
      {/* UX_PLAN.md P1: the old program's name for the account, as evidence
          under the chart name — it is searchable, so it is also shown. */}
      {showsImportLabel(option) && (
        <span
          className="account-option__import-label"
          style={{ display: 'block', fontSize: '0.75rem', color: 'var(--secondary, #717579)' }}
        >
          I importfilen: {option.importSourceName}
        </span>
      )}
    </span>
  );
}
